import React, { useEffect, useState } from "react";
import axios from "axios";
import { Button } from "react-bootstrap";
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faPlus, faTrash, faFileLines } from "@fortawesome/free-solid-svg-icons";

const MONTH_NAMES = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
];

const API_BASE = 'https://api.ps-link.world/api/plexus/tempsalary';
const EMPLOYEE_API = 'https://api.ps-link.world/api/plexus/employee/read';

const EMPTY_FORM = {
    month: '',
    year: new Date().getFullYear(),
    employeeId: '',
    name: '',
    totalSalary: 0,

    // Each entry = one calendar date marked absent, either a Full Day (1)
    // or Half Day (0.5), with its own reason.
    absentEntries: [],

    freeLeaveDays: '0',
    freeLeaveReason: '',

    // Each entry = "I am paying back THIS specific absent date (from an
    // earlier month)". `dayType` here is the AMOUNT being paid back by
    // THIS entry ('full' or 'half') - a single Full Day absence can be
    // split into two Half Day cover entries, each on its own `coverDate`.
    coverEntries: [],
    coverReason: ''
};

const dayValue = (dayType) => (dayType === 'half' ? 0.5 : 1);

const getDaysInMonth = (month, year) => {
    if (!month || !year) return 0;
    return new Date(Number(year), Number(month), 0).getDate();
};

// yyyy-mm-dd bounds for <input type="date"> so the user can only pick a
// date that actually falls inside the month/year selected above.
const monthDateBounds = (month, year) => {
    if (!month || !year) return { min: '', max: '' };
    const m = String(month).padStart(2, '0');
    const lastDay = getDaysInMonth(month, year);
    return {
        min: `${year}-${m}-01`,
        max: `${year}-${m}-${String(lastDay).padStart(2, '0')}`
    };
};

const getEmployeeId = (emp) => {
    if (!emp) return '';
    if (typeof emp === 'object') return String(emp._id ?? '');
    return String(emp);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr + 'T00:00:00');
    if (isNaN(d.getTime())) return dateStr;
    return `${String(d.getDate()).padStart(2, '0')} ${MONTH_NAMES[d.getMonth()].slice(0, 3)} ${d.getFullYear()}`;
};

const round2 = (n) => Number((Number(n) || 0).toFixed(2));

let nextEntryKey = 1;

const TempSalary = () => {
    const [visible, setVisible] = useState(false);
    const [id, setId] = useState();
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [filteredData, setFilteredData] = useState([]);
    const [employeesList, setEmployeesList] = useState([]);
    const [markingReadIds, setMarkingReadIds] = useState(new Set());

    // Pending (not-yet-fully-covered) absent entries for the currently
    // selected employee. Each item = { sourceRecordId, absentEntryId, date,
    // month, year, dayType, pendingDays, label }. `pendingDays` is how much
    // of that date is STILL uncovered (1 = whole day pending, 0.5 = only
    // half still pending because the other half was already covered
    // elsewhere). Refetched whenever employee/month/year/id changes.
    const [pendingCovers, setPendingCovers] = useState([]);
    const [loadingPendingCovers, setLoadingPendingCovers] = useState(false);

    const [formData, setFormData] = useState(EMPTY_FORM);

    const [reasonModalRecord, setReasonModalRecord] = useState(null);

    useEffect(() => {
        if (visible) window.history.pushState({ modalOpen: true }, '');
        const handlePopState = () => { if (visible) closeModal(); };
        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [visible]);

    const closeModal = () => {
        setVisible(false);
        setErrors({});
        setFormData(EMPTY_FORM);
        setPendingCovers([]);
        setId(undefined);
    };

    const toggleModal = (mode) => {
        if (isSubmitting) return;

        if (visible) {
            window.history.back();
            return;
        }

        if (mode === 'add') {
            setFormData(EMPTY_FORM);
            setPendingCovers([]);
            setId(undefined);
        }

        setErrors({});
        setVisible(true);
    };

    const handleFormKeyDown = (e) => {
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
            e.preventDefault();
        }
    };

    const getEmployees = async () => {
        try {
            const response = await axios.get(EMPLOYEE_API);
            setEmployeesList(response.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch employee data.");
        }
    };

    const getData = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${API_BASE}/read`);
            setFilteredData(response.data.data || []);
        } catch (err) {
            console.error(err);
            toast.error("Failed to fetch salary data.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getEmployees();
        getData();
    }, []);

    useEffect(() => {
        if (!formData.employeeId) {
            setPendingCovers([]);
            return;
        }

        let cancelled = false;
        setLoadingPendingCovers(true);

        // excludeRecordId: when editing an existing record, its own cover
        // entries are already saved on the source dates in the DB (they only
        // get released on actual save). Without telling the backend which
        // record is being edited, those dates would look "already fully
        // covered" and silently vanish from the dropdown even though this
        // very form already has them selected.
        axios.get(`${API_BASE}/pendingcovers/${formData.employeeId}`, {
            params: { excludeMonth: formData.month, excludeYear: formData.year, excludeRecordId: id }
        })
            .then(res => {
                if (cancelled) return;
                setPendingCovers(res.data.data || []);
            })
            .catch(err => {
                console.error(err);
                if (!cancelled) toast.error("Failed to fetch pending absence dates.");
            })
            .finally(() => {
                if (!cancelled) setLoadingPendingCovers(false);
            });

        return () => { cancelled = true; };
    }, [formData.employeeId, formData.month, formData.year, id]);

    const handleField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSelectEmployee = (employeeId) => {
        const emp = employeesList.find(e => String(e._id) === String(employeeId));
        setFormData(prev => ({
            ...prev,
            employeeId,
            name: emp?.name || '',
            totalSalary: Number(emp?.salary ?? emp?.totalSalary ?? 0),
            coverEntries: []
        }));
        if (errors.employeeId) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.employeeId;
                return newErrors;
            });
        }
    };

    // ---- Absent entries (dynamic list: one row per absent date) ----
    const addAbsentEntry = () => {
        setFormData(prev => ({
            ...prev,
            absentEntries: [...prev.absentEntries, { key: nextEntryKey++, date: '', dayType: 'full', reason: '' }]
        }));
    };

    const removeAbsentEntry = (key) => {
        setFormData(prev => ({
            ...prev,
            absentEntries: prev.absentEntries.filter(e => e.key !== key)
        }));
    };

    const updateAbsentEntry = (key, field, value) => {
        setFormData(prev => ({
            ...prev,
            absentEntries: prev.absentEntries.map(e => e.key === key ? { ...e, [field]: value } : e)
        }));

        const errorKey = `absent_${key}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    const usedAbsentDates = (currentKey) => new Set(
        formData.absentEntries
            .filter(e => e.key !== currentKey && e.date)
            .map(e => e.date)
    );

    // ---- Cover entries (dynamic list: one row per cover action) ----
    // A row = "pay back {dayType} of {absentEntryId}, and this cover was
    // made on {coverDate}". Multiple rows CAN point at the same
    // absentEntryId (e.g. two Half Day rows both covering one Full Day
    // absence, each on a different coverDate) as long as the combined
    // amount doesn't exceed what's still pending on that date.
    const addCoverEntry = () => {
        setFormData(prev => ({
            ...prev,
            coverEntries: [...prev.coverEntries, {
                key: nextEntryKey++,
                absentEntryId: '', sourceRecordId: '', date: '', month: '', year: '',
                dayType: '', coverDate: ''
            }]
        }));
    };

    const removeCoverEntry = (key) => {
        setFormData(prev => ({
            ...prev,
            coverEntries: prev.coverEntries.filter(e => e.key !== key)
        }));
    };

    // How much of a given absent date is still available to allocate,
    // counting every OTHER cover row in this form that already targets it
    // (but not the row currently being edited, so its own dropdown still
    // shows the room it's allowed to use).
    const remainingPendingFor = (absentEntryId, currentKey) => {
        const base = pendingCovers.find(p => p.absentEntryId === absentEntryId);
        if (!base) return 0;
        const usedByOtherRows = formData.coverEntries
            .filter(e => e.key !== currentKey && e.absentEntryId === absentEntryId)
            .reduce((sum, e) => sum + (e.dayType ? dayValue(e.dayType) : 0), 0);
        return round2(base.pendingDays - usedByOtherRows);
    };

    // Selecting a date fills in sourceRecordId/date/month/year, and
    // defaults the amount to whatever's fully available (Full Day if a
    // whole day is open, Half Day if only half remains).
    const updateCoverEntryDate = (key, absentEntryId) => {
        const picked = pendingCovers.find(p => p.absentEntryId === absentEntryId);

        setFormData(prev => ({
            ...prev,
            coverEntries: prev.coverEntries.map(e => {
                if (e.key !== key) return e;
                if (!picked) return { ...e, absentEntryId: '', sourceRecordId: '', date: '', month: '', year: '', dayType: '' };
                const remaining = remainingPendingFor(absentEntryId, key);
                return {
                    ...e,
                    absentEntryId: picked.absentEntryId,
                    sourceRecordId: picked.sourceRecordId,
                    date: picked.date,
                    month: picked.month,
                    year: picked.year,
                    dayType: remaining >= 1 ? 'full' : 'half'
                };
            })
        }));

        const errorKey = `cover_${key}_absentEntryId`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    const updateCoverEntryField = (key, field, value) => {
        setFormData(prev => ({
            ...prev,
            coverEntries: prev.coverEntries.map(e => e.key === key ? { ...e, [field]: value } : e)
        }));

        const errorKey = `cover_${key}_${field}`;
        if (errors[errorKey]) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[errorKey];
                return newErrors;
            });
        }
    };

    // Dropdown options for a cover row: every pending absent date that
    // still has room left (after subtracting what OTHER rows already
    // claimed), always including this row's own current selection.
    const availableCoversFor = (currentKey) => {
        const current = formData.coverEntries.find(e => e.key === currentKey);
        return pendingCovers
            .filter(p => remainingPendingFor(p.absentEntryId, currentKey) > 0 || current?.absentEntryId === p.absentEntryId)
            .map(p => ({ ...p, remaining: remainingPendingFor(p.absentEntryId, currentKey) }));
    };

    // Amount options for a row's Full/Half select: if a whole day is still
    // open, both are offered; if only half remains, Half Day is forced.
    const coverAmountOptionsFor = (entry) => {
        const remaining = remainingPendingFor(entry.absentEntryId, entry.key);
        if (remaining >= 1) return [{ value: 'full', label: 'Full Day' }, { value: 'half', label: 'Half Day' }];
        if (remaining >= 0.5) return [{ value: 'half', label: 'Half Day' }];
        return [];
    };

    const canAddMoreCoverEntries = pendingCovers.some(p => remainingPendingFor(p.absentEntryId, null) > 0);

    // ---- Live preview (mirrors backend calc for display purposes) ----
    const totalDays = getDaysInMonth(formData.month, formData.year);
    const perDaySalary = totalDays ? Number(formData.totalSalary || 0) / totalDays : 0;

    const totalAbsentDays = round2(formData.absentEntries.reduce((sum, e) => sum + dayValue(e.dayType), 0));
    const cutSalary = round2(totalAbsentDays * perDaySalary);

    const totalCoverDays = round2(formData.coverEntries.reduce((sum, e) => sum + (e.dayType ? dayValue(e.dayType) : 0), 0));
    const coverAmount = round2(formData.coverEntries.reduce((sum, e) => {
        if (!e.absentEntryId || !e.dayType) return sum;
        const source = filteredData.find(r => r._id === e.sourceRecordId);
        if (!source) return sum;
        const srcPerDaySalary = Number(source.totalSalary || 0) / getDaysInMonth(e.month, e.year);
        return sum + dayValue(e.dayType) * srcPerDaySalary;
    }, 0));

    const previewPaySalary = round2(Number(formData.totalSalary || 0) - cutSalary + coverAmount);

    const isValidNumberString = (val) => val !== '' && !isNaN(Number(val)) && Number(val) >= 0;

    const validate = () => {
        const newErrors = {};

        if (!formData.month) newErrors.month = 'Month is required';
        if (!formData.year) newErrors.year = 'Year is required';
        if (!formData.employeeId) newErrors.employeeId = 'Select an employee';

        const duplicate = filteredData.some(r =>
            r._id !== id &&
            getEmployeeId(r.employeeId) === String(formData.employeeId) &&
            String(r.month) === String(formData.month) &&
            String(r.year) === String(formData.year)
        );
        if (!newErrors.employeeId && duplicate) {
            newErrors.employeeId = 'This employee already has a record for this month';
        }

        if (!isValidNumberString(formData.freeLeaveDays)) newErrors.freeLeaveDays = 'Enter a valid number (e.g. 1)';
        if (Number(formData.freeLeaveDays || 0) > 0 && !formData.freeLeaveReason.trim()) {
            newErrors.freeLeaveReason = 'Reason is required when free leave days > 0';
        }

        formData.absentEntries.forEach(entry => {
            if (!entry.date) {
                newErrors[`absent_${entry.key}_date`] = 'Select a date';
            }
            if (!entry.reason || !entry.reason.trim()) {
                newErrors[`absent_${entry.key}_reason`] = 'Reason is required';
            }
        });
        const seenDates = new Set();
        formData.absentEntries.forEach(entry => {
            if (entry.date && seenDates.has(entry.date)) {
                newErrors[`absent_${entry.key}_date`] = 'This date is already added';
            }
            if (entry.date) seenDates.add(entry.date);
        });

        // Cover rows: each needs a date, an amount, and a "covered on" date.
        // Also make sure the total requested per absentEntryId never
        // exceeds what's actually still pending for that date.
        const coverTotals = {};
        formData.coverEntries.forEach(entry => {
            if (entry.absentEntryId) {
                coverTotals[entry.absentEntryId] = round2(
                    (coverTotals[entry.absentEntryId] || 0) + (entry.dayType ? dayValue(entry.dayType) : 0)
                );
            }
        });
        formData.coverEntries.forEach(entry => {
            if (!entry.absentEntryId) {
                newErrors[`cover_${entry.key}_absentEntryId`] = 'Select a date to cover';
                return;
            }
            if (!entry.dayType) {
                newErrors[`cover_${entry.key}_dayType`] = 'Select Full or Half Day';
            }
            if (!entry.coverDate) {
                newErrors[`cover_${entry.key}_coverDate`] = 'Select the date this was covered on';
            }
            const base = pendingCovers.find(p => p.absentEntryId === entry.absentEntryId);
            if (base && coverTotals[entry.absentEntryId] > base.pendingDays + 0.0001) {
                newErrors[`cover_${entry.key}_absentEntryId`] = 'Total cover exceeds the pending amount for this date';
            }
        });

        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();

        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }

        try {
            setIsSubmitting(true);

            const payload = {
                month: formData.month,
                year: formData.year,
                employeeId: formData.employeeId,
                name: formData.name,
                totalSalary: Number(formData.totalSalary || 0),

                absentEntries: formData.absentEntries.map(e => ({
                    date: e.date,
                    dayType: e.dayType,
                    reason: e.reason.trim()
                })),

                freeLeaveDays: Number(formData.freeLeaveDays || 0),
                freeLeaveReason: formData.freeLeaveReason.trim(),

                coverEntries: formData.coverEntries
                    .filter(e => e.absentEntryId && e.dayType)
                    .map(e => ({
                        sourceRecordId: e.sourceRecordId,
                        absentEntryId: e.absentEntryId,
                        dayType: e.dayType,
                        coverDate: e.coverDate
                    })),
                coverReason: formData.coverReason.trim()
            };

            const endpoint = id
                ? `${API_BASE}/update/${id}`
                : `${API_BASE}/create`;
            const method = id ? 'patch' : 'post';

            const response = await axios[method](endpoint, payload);

            toast.success(response.data.message || (id ? 'Salary updated successfully!' : 'Salary created successfully!'));
            resetForm();
            getData();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || "An error occurred. Please try again.");
        } finally {
            setIsSubmitting(false);
        }
    };

    const resetForm = () => {
        if (visible) {
            window.history.back();
        } else {
            closeModal();
        }
    };

    const handleEdit = (record) => {
        if (isSubmitting) return;

        const absentEntries = (record.absentEntries || []).map(e => ({
            key: nextEntryKey++,
            date: e.date,
            dayType: e.dayType || 'full',
            reason: e.reason || ''
        }));

        const coverEntries = (record.coverEntries || []).map(e => ({
            key: nextEntryKey++,
            absentEntryId: e.absentEntryId,
            sourceRecordId: e.sourceRecordId,
            date: e.date,
            month: e.month,
            year: e.year,
            dayType: e.dayType,
            coverDate: e.coverDate || ''
        }));

        setFormData({
            month: record.month,
            year: record.year,
            employeeId: getEmployeeId(record.employeeId),
            name: record.name || '',
            totalSalary: Number(record.totalSalary || 0),

            absentEntries,

            freeLeaveDays: String(record.freeLeaveDays ?? '0'),
            freeLeaveReason: record.freeLeaveReason || '',

            coverEntries,
            coverReason: record.coverReason || ''
        });
        setId(record._id);
        setErrors({});
        setVisible(true);
    };

    const handleDelete = async (recordId) => {
        if (!isSubmitting && window.confirm("Are you sure you want to delete this salary record?")) {
            try {
                setIsSubmitting(true);
                const response = await axios.delete(`${API_BASE}/delete/${recordId}`);
                toast.success(response.data.message || 'Salary record deleted successfully!');
                getData();
            } catch (err) {
                console.error(err);
                toast.error(err.response?.data?.message || "An error occurred. Please try again.");
            } finally {
                setIsSubmitting(false);
            }
        }
    };

    const handleMarkRead = async (recordId, currentRead) => {
        if (!window.confirm("Are you sure you want to update the read status of this record?")) return;
        try {
            setMarkingReadIds(prev => new Set(prev).add(recordId));

            const response = await axios.patch(
                `${API_BASE}/update/${recordId}`,
                {
                    read: !currentRead
                }
            );

            toast.success(response.data.message || "Status updated!");
            getData();
        } catch (err) {
            console.error(err);
            toast.error(
                err.response?.data?.message || "An error occurred. Please try again."
            );
        } finally {
            setMarkingReadIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(recordId);
                return newSet;
            });
        }
    };

    const dateBounds = monthDateBounds(formData.month, formData.year);

    if (loading) return (
        <div
            style={{
                height: '80vh',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                overflow: "hidden"
            }}
        >
            <div className="border p-4 flex items-center space-x-2 rounded-md">
                <div className="w-10 h-10 border-2 border-gray-300 rounded-full animate-spin dark:border-gray-800" style={{ borderTop: "2px solid #0777AB" }}></div>
            </div>
        </div>
    );

    return (
        <div>
            <div className="space-y-6 sticky left-0">
                <div
                    className={`rounded-2xl overflow-auto border-gray-200 bg-white dark:border-gray-800 dark:bg-white/[0.03]`}
                    style={{ minHeight: "600px" }}
                >
                    {/* Card Header */}
                    <div className="px-6 pt-5">
                        <div className="flex justify-between items-center px-4 py-3 mt-4 dark:border-gray-800 border-gray-200">
                            <div className="flex justify-end">
                                <div className="flex items-center gap-2">
                                    <Button
                                        onClick={() => toggleModal('add')}
                                        className="rounded-md border-0 shadow-md px-4 py-2 text-white"
                                        style={{ background: "#0777AB" }}
                                    >
                                        <FontAwesomeIcon icon={faPlus} className='pe-2' /> Add Salary
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Card Body - one row per employee per month */}
                    <div className="p-4 border-gray-100 dark:border-gray-800 sm:p-6 overflow-auto">
                        <div className="space-y-6 rounded-lg xl:border dark:border-gray-800">
                            <table className="w-full table-fixed border-collapse text-sm">
                                <colgroup>
                                    <col className="w-12" />
                                    <col className="w-32" />
                                    <col className="w-28" />
                                    <col className="w-28" />
                                    <col className="w-24" />
                                    <col className="w-32" />
                                    <col className="w-32" />
                                    <col className="w-20" />
                                    <col className="w-24" />
                                    <col className="w-24" />
                                    <col className="w-24" />
                                    <col className="w-28" />
                                </colgroup>
                                <thead className="border-b border-gray-100 dark:border-white/[0.05]">
                                    <tr>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">#</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Employee</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Month / Year</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Absent</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Free Leave</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Cover Leave</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Reason</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Total Salary</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Cut Salary</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Pay Salary</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Status</th>
                                        <th className="py-4 font-medium text-gray-500 dark:text-gray-300 px-2 border-r border-gray-200 dark:border-gray-700 text-center">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100 dark:divide-white/[0.05] text-center">
                                    {filteredData.length > 0 ? (
                                        filteredData.map((record, index) => {
                                            const isMarkingRead = markingReadIds.has(record._id);
                                            const recordAbsentDays = round2((record.absentEntries || []).reduce((sum, e) => sum + dayValue(e.dayType), 0));
                                            const recordCoverDays = round2((record.coverEntries || []).reduce((sum, e) => sum + dayValue(e.dayType), 0));
                                            return (
                                                <tr key={record._id} className={`transition-opacity duration-200 ${record.read ? 'opacity-40' : ''}`}>
                                                    <td className="text-center px-2 py-3 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        {index + 1}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        {record.name}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        {MONTH_NAMES[Number(record.month) - 1]} {record.year}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        {recordAbsentDays}
                                                        {/* {(record.absentEntries || []).length > 0 && (
                                                            <span className="block text-xs text-gray-400">
                                                                {record.absentEntries.map(e => `${formatDate(e.date)} (${e.dayType === 'half' ? '½' : '1'}d)`).join(', ')}
                                                            </span>
                                                        )} */}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200" title={record.freeLeaveReason}>
                                                        {record.freeLeaveDays || 0}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200" title={record.coverReason}>
                                                        {recordCoverDays}
                                                        {/* {(record.coverEntries || []).length > 0 && (
                                                            <span className="block text-xs text-gray-400">
                                                                {record.coverEntries.map((e, i) => (
                                                                    <React.Fragment key={i}>
                                                                        {e.dayType === 'half' ? '½' : '1'}d of {formatDate(e.date)}{e.coverDate ? ` (on ${formatDate(e.coverDate)})` : ''}
                                                                        {i < record.coverEntries.length - 1 ? ', ' : ''}
                                                                    </React.Fragment>
                                                                ))}
                                                            </span>
                                                        )} */}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        {((record.absentEntries || []).length > 0 || record.freeLeaveReason || (record.coverEntries || []).length > 0) ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => setReasonModalRecord(record)}
                                                                className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md hover:underline"
                                                                style={{ color: "#0385C3" }}
                                                                title="View reason"
                                                            >
                                                                <FontAwesomeIcon icon={faFileLines} /> View
                                                            </button>
                                                        ) : (
                                                            <span className="text-gray-300">-</span>
                                                        )}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 font-medium text-blue-600 dark:text-blue-400">
                                                        {record.totalSalary}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 font-medium text-red-600 dark:text-red-400">
                                                        {record.cutSalary}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 font-medium text-green-600 dark:text-green-400">
                                                        {record.paySalary}
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        <button
                                                            onClick={() => handleMarkRead(record._id, record.read)}
                                                            className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:hover:bg-blue-900/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                        >
                                                            {isMarkingRead ? "Updating..." : (record.read ? "Mark Unpaid" : "Mark Paid")}
                                                        </button>
                                                    </td>
                                                    <td className="py-3 px-2 border-r border-gray-200 dark:border-gray-700 dark:text-gray-200">
                                                        <div className="flex align-middle justify-center gap-4">
                                                            <button style={{ color: "#0385C3" }} onClick={() => handleEdit(record)} title="Edit record">
                                                                <FontAwesomeIcon icon={faEdit} className="text-lg" />
                                                            </button>
                                                            <button className="text-red-600" onClick={() => handleDelete(record._id)} title="Delete record">
                                                                <FontAwesomeIcon icon={faTrash} className="text-lg" />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan={12} className="text-center pt-5 pb-4 dark:text-gray-200">No Data Found</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            {/* Modal for Add/Edit Salary */}
            {visible && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={() => !isSubmitting && toggleModal('add')}
                    ></div>

                    <div className="relative bg-white rounded-lg w-full max-w-3xl mx-4 dark:bg-gray-800 dark:text-gray-200 max-h-[90vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b">
                            <h3 className="text-xl font-semibold">
                                {id ? "Edit Salary" : "Add Salary"}
                            </h3>
                        </div>

                        <div className="px-6 py-4">
                            <form onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                                    {/* Employee */}
                                    <div>
                                        <label className="block font-medium mb-2">
                                            Employee
                                            <span className="text-red-500 pl-2 font-normal text-lg">*</span>
                                        </label>
                                        <select
                                            value={formData.employeeId}
                                            onChange={(e) => handleSelectEmployee(e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.employeeId ? 'border-red-500' : 'border-gray-300'}`}
                                            disabled={isSubmitting || !!id}
                                        >
                                            <option value="">Select employee</option>
                                            {employeesList.map((emp) => (
                                                <option key={emp._id} value={emp._id}>{emp.name}</option>
                                            ))}
                                        </select>
                                        {errors.employeeId && (
                                            <p className="text-red-500 text-sm mt-1">{errors.employeeId}</p>
                                        )}
                                    </div>

                                    {/* Month */}
                                    <div>
                                        <label className="block font-medium mb-2">
                                            Month
                                            <span className="text-red-500 pl-2 font-normal text-lg">*</span>
                                        </label>
                                        <select
                                            value={formData.month}
                                            onChange={(e) => handleField('month', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.month ? 'border-red-500' : 'border-gray-300'}`}
                                            disabled={isSubmitting}
                                        >
                                            <option value="">Select month</option>
                                            {MONTH_NAMES.map((m, i) => (
                                                <option key={m} value={i + 1}>{m}</option>
                                            ))}
                                        </select>
                                        {errors.month && (
                                            <p className="text-red-500 text-sm mt-1">{errors.month}</p>
                                        )}
                                    </div>

                                    {/* Year */}
                                    <div>
                                        <label className="block font-medium mb-2">
                                            Year
                                            <span className="text-red-500 pl-2 font-normal text-lg">*</span>
                                        </label>
                                        <input
                                            type="number"
                                            value={formData.year}
                                            onChange={(e) => handleField('year', e.target.value)}
                                            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.year ? 'border-red-500' : 'border-gray-300'}`}
                                            placeholder="Enter year"
                                            disabled={isSubmitting}
                                        />
                                        {errors.year && (
                                            <p className="text-red-500 text-sm mt-1">{errors.year}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Total Salary preview */}
                                <div className="mb-4">
                                    <label className="block text-sm font-medium mb-1">Total Salary</label>
                                    <input
                                        type="text"
                                        readOnly
                                        value={formData.employeeId ? formData.totalSalary : ''}
                                        className="w-full sm:w-1/3 px-3 py-2 border rounded-lg bg-gray-100 dark:bg-gray-800 dark:border-gray-600 dark:text-white"
                                    />
                                </div>

                                {/* ---- Box 1: Absent (dynamic list of dates) ---- */}
                                <div className="mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700/40 dark:border-gray-600">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="font-medium">Absent (cuts salary)</p>
                                        <button
                                            type="button"
                                            onClick={addAbsentEntry}
                                            disabled={isSubmitting || !formData.month || !formData.year}
                                            className="text-sm px-3 py-1 rounded-md text-white disabled:opacity-40"
                                            style={{ backgroundColor: "#0777AB" }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="pe-1" /> Add Date
                                        </button>
                                    </div>

                                    {(!formData.month || !formData.year) && (
                                        <p className="text-gray-400 text-xs mb-2">Select month and year first.</p>
                                    )}

                                    {formData.absentEntries.map((entry) => {
                                        const used = usedAbsentDates(entry.key);
                                        return (
                                            <div key={entry.key} className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3 items-start">
                                                <div className="sm:col-span-4">
                                                    <input
                                                        type="date"
                                                        min={dateBounds.min}
                                                        max={dateBounds.max}
                                                        value={entry.date}
                                                        onChange={(e) => updateAbsentEntry(entry.key, 'date', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors[`absent_${entry.key}_date`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        disabled={isSubmitting}
                                                    />
                                                    {used.has(entry.date) && entry.date && (
                                                        <p className="text-red-500 text-sm mt-1">This date is already added</p>
                                                    )}
                                                    {errors[`absent_${entry.key}_date`] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[`absent_${entry.key}_date`]}</p>
                                                    )}
                                                </div>
                                                <div className="sm:col-span-3">
                                                    <select
                                                        value={entry.dayType}
                                                        onChange={(e) => updateAbsentEntry(entry.key, 'dayType', e.target.value)}
                                                        className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white border-gray-300"
                                                        disabled={isSubmitting}
                                                    >
                                                        <option value="full">Full Day</option>
                                                        <option value="half">Half Day</option>
                                                    </select>
                                                </div>
                                                <div className="sm:col-span-4">
                                                    <input
                                                        type="text"
                                                        placeholder="Reason for this date"
                                                        value={entry.reason}
                                                        onChange={(e) => updateAbsentEntry(entry.key, 'reason', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors[`absent_${entry.key}_reason`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        disabled={isSubmitting}
                                                    />
                                                    {errors[`absent_${entry.key}_reason`] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[`absent_${entry.key}_reason`]}</p>
                                                    )}
                                                </div>
                                                <div className="sm:col-span-1 flex items-center h-full">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeAbsentEntry(entry.key)}
                                                        disabled={isSubmitting}
                                                        className="text-red-600 mt-1"
                                                        title="Remove this date"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {formData.absentEntries.length > 0 && (
                                        <p className="text-xs text-gray-400 mt-1">
                                            Total absent: {totalAbsentDays} day(s)
                                        </p>
                                    )}
                                </div>

                                {/* ---- Box 2: Free Leave ---- */}
                                <div className="mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700/40 dark:border-gray-600">
                                    <p className="font-medium mb-3">Free Leave (no salary cut)</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Free Leave Days</label>
                                            <input
                                                type="text"
                                                inputMode="decimal"
                                                placeholder="e.g. 1"
                                                value={formData.freeLeaveDays}
                                                onChange={(e) => {
                                                    if (e.target.value === '' || /^\d*\.?\d*$/.test(e.target.value)) {
                                                        handleField('freeLeaveDays', e.target.value);
                                                    }
                                                }}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.freeLeaveDays ? 'border-red-500' : 'border-gray-300'}`}
                                                disabled={isSubmitting}
                                            />
                                            {errors.freeLeaveDays && <p className="text-red-500 text-sm mt-1">{errors.freeLeaveDays}</p>}
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-1">Reason</label>
                                            <textarea
                                                rows={4}
                                                value={formData.freeLeaveReason}
                                                onChange={(e) => handleField('freeLeaveReason', e.target.value)}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors.freeLeaveReason ? 'border-red-500' : 'border-gray-300'}`}
                                                placeholder="Why free leave"
                                                disabled={isSubmitting}
                                            />
                                            {errors.freeLeaveReason && <p className="text-red-500 text-sm mt-1">{errors.freeLeaveReason}</p>}
                                        </div>
                                    </div>
                                </div>

                                {/* ---- Box 3: Cover (pick specific earlier absent dates to pay back) ---- */}
                                <div className="mb-4 p-4 border rounded-lg bg-gray-50 dark:bg-gray-700/40 dark:border-gray-600">
                                    <div className="flex items-center justify-between mb-3">
                                        <p className="font-medium">Cover (pays back an earlier absent date)</p>
                                        <button
                                            type="button"
                                            onClick={addCoverEntry}
                                            disabled={isSubmitting || !formData.employeeId || !canAddMoreCoverEntries}
                                            className="text-sm px-3 py-1 rounded-md text-white disabled:opacity-40"
                                            style={{ backgroundColor: "#0777AB" }}
                                        >
                                            <FontAwesomeIcon icon={faPlus} className="pe-1" /> Add Cover
                                        </button>
                                    </div>

                                    {!formData.employeeId && (
                                        <p className="text-gray-400 text-xs mb-2">Select an employee first.</p>
                                    )}
                                    {formData.employeeId && loadingPendingCovers && (
                                        <p className="text-gray-400 text-xs mb-2">Loading pending absent dates...</p>
                                    )}
                                    {formData.employeeId && !loadingPendingCovers && pendingCovers.length === 0 && (
                                        <p className="text-gray-400 text-xs mb-2">No past uncovered absence for this employee.</p>
                                    )}

                                    {formData.coverEntries.map((entry) => {
                                        const amountOptions = coverAmountOptionsFor(entry);
                                        return (
                                            <div key={entry.key} className="grid grid-cols-1 sm:grid-cols-12 gap-3 mb-3 items-start">
                                                {/* Which absent date is being paid back */}
                                                <div className="sm:col-span-5">
                                                    <select
                                                        value={entry.absentEntryId}
                                                        onChange={(e) => updateCoverEntryDate(entry.key, e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors[`cover_${entry.key}_absentEntryId`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        disabled={isSubmitting}
                                                    >
                                                        <option value="">Select date to cover</option>
                                                        {availableCoversFor(entry.key).map((p) => (
                                                            <option key={p.absentEntryId} value={p.absentEntryId}>
                                                                {formatDate(p.date)} - {p.remaining} day(s) pending
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors[`cover_${entry.key}_absentEntryId`] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[`cover_${entry.key}_absentEntryId`]}</p>
                                                    )}
                                                </div>

                                                {/* How much of it (Full/Half) this row pays back */}
                                                <div className="sm:col-span-3">
                                                    <select
                                                        value={entry.dayType}
                                                        onChange={(e) => updateCoverEntryField(entry.key, 'dayType', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors[`cover_${entry.key}_dayType`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        disabled={isSubmitting || !entry.absentEntryId}
                                                    >
                                                        <option value="">Amount</option>
                                                        {amountOptions.map(opt => (
                                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                                        ))}
                                                    </select>
                                                    {errors[`cover_${entry.key}_dayType`] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[`cover_${entry.key}_dayType`]}</p>
                                                    )}
                                                </div>

                                                {/* Date on which THIS cover was made */}
                                                <div className="sm:col-span-3">
                                                    <input
                                                        type="date"
                                                        min={dateBounds.min}
                                                        max={dateBounds.max}
                                                        value={entry.coverDate}
                                                        onChange={(e) => updateCoverEntryField(entry.key, 'coverDate', e.target.value)}
                                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white ${errors[`cover_${entry.key}_coverDate`] ? 'border-red-500' : 'border-gray-300'}`}
                                                        disabled={isSubmitting || !entry.absentEntryId}
                                                        title="Date this cover was made on"
                                                    />
                                                    {errors[`cover_${entry.key}_coverDate`] && (
                                                        <p className="text-red-500 text-sm mt-1">{errors[`cover_${entry.key}_coverDate`]}</p>
                                                    )}
                                                </div>

                                                <div className="sm:col-span-1 flex items-center h-full">
                                                    <button
                                                        type="button"
                                                        onClick={() => removeCoverEntry(entry.key)}
                                                        disabled={isSubmitting}
                                                        className="text-red-600 mt-1"
                                                        title="Remove this cover entry"
                                                    >
                                                        <FontAwesomeIcon icon={faTrash} />
                                                    </button>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {formData.coverEntries.length > 0 && (
                                        <div className="mt-2">
                                            <label className="block text-sm font-medium mb-1">Reason (optional, applies to all cover entries above)</label>
                                            <input
                                                type="text"
                                                value={formData.coverReason}
                                                onChange={(e) => handleField('coverReason', e.target.value)}
                                                className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white border-gray-300"
                                                placeholder="Note about these covers"
                                                disabled={isSubmitting}
                                            />
                                        </div>
                                    )}
                                </div>

                                {/* Live preview */}
                                <div className="mb-6 p-4 border rounded-lg dark:border-gray-600">
                                    <p className="font-medium mb-3">Preview</p>

                                    <div className="overflow-x-auto">
                                        <table className="w-full border border-gray-300 dark:border-gray-600 text-sm text-center">
                                            <thead>
                                                <tr className="bg-gray-100 dark:bg-gray-700">
                                                    <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                                                        Per Day Salary
                                                    </th>
                                                    <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                                                        Cut Salary ({totalAbsentDays} day(s))
                                                    </th>
                                                    <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                                                        Cover Added ({totalCoverDays} day(s))
                                                    </th>
                                                    <th className="border border-gray-300 dark:border-gray-600 px-3 py-2">
                                                        Pay Salary (Approx.)
                                                    </th>
                                                </tr>
                                            </thead>

                                            <tbody>
                                                <tr>
                                                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 font-medium">
                                                        {totalDays ? perDaySalary.toFixed(2) : "-"}
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 font-medium text-red-600">
                                                        {cutSalary}
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 font-medium text-blue-600">
                                                        {coverAmount}
                                                    </td>
                                                    <td className="border border-gray-300 dark:border-gray-600 px-3 py-3 font-bold text-green-600 dark:text-green-400">
                                                        {previewPaySalary}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex gap-4 mt-6">
                                    <button
                                        type="button"
                                        onClick={() => toggleModal()}
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 bg-gray-100 text-black rounded-lg hover:bg-gray-200 transition-colors duration-200"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="w-1/2 py-2 px-4 text-white rounded-lg transition-colors duration-200"
                                        style={{ backgroundColor: "#0777AB" }}
                                    >
                                        {isSubmitting ? (
                                            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                                        ) : (
                                            id ? 'Update' : 'Submit'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* View-reason popup */}
            {reasonModalRecord !== null && (
                <div className="fixed inset-0 z-99999 flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50"
                        onClick={() => setReasonModalRecord(null)}
                    ></div>
                    <div className="relative bg-white rounded-lg w-full max-w-lg mx-4 dark:bg-gray-800 dark:text-gray-200 max-h-[80vh] overflow-y-auto">
                        <div className="px-6 py-4 border-b flex items-center justify-between">
                            <h3 className="text-lg font-semibold">Reason</h3>
                            <button
                                onClick={() => setReasonModalRecord(null)}
                                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-xl leading-none"
                                title="Close"
                            >
                                &times;
                            </button>
                        </div>
                        <div className="px-6 py-4 space-y-4">
                            {(reasonModalRecord.absentEntries || []).length > 0 && (
                                <div>
                                    <div className="text-xs font-semibold text-blue-500 uppercase mb-2">
                                        Absent
                                    </div>
                                    <div className="space-y-3">
                                        {reasonModalRecord.absentEntries.map((e, i) => (
                                            <div key={i} className="border-l-2 pl-3" style={{ borderColor: "#0777AB" }}>
                                                <div className="text-sm font-medium">
                                                    {formatDate(e.date)} - {e.dayType === 'half' ? 'Half Day' : 'Full Day'}
                                                </div>
                                                <pre
                                                    className="font-sans text-sm text-gray-600 dark:text-gray-300"
                                                    style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
                                                >
                                                    {e.reason}
                                                </pre>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {(reasonModalRecord.coverEntries || []).length > 0 && (
                                <div>
                                    <div className="text-xs font-semibold text-blue-500 uppercase mb-2 pt-2">
                                        Cover
                                    </div>
                                    <div className="space-y-3">
                                        {reasonModalRecord.coverEntries.map((e, i) => (
                                            <div key={i} className="border-l-2 pl-3" style={{ borderColor: "#16a34a" }}>
                                                <div className="text-sm font-medium">
                                                    {e.dayType === 'half' ? 'Half Day' : 'Full Day'} Cover
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                                    Absent Date: <span className="font-medium">{formatDate(e.date)}</span>
                                                </div>
                                                <div className="text-sm text-gray-600 dark:text-gray-300">
                                                    Covered On: <span className="font-medium">{e.coverDate ? formatDate(e.coverDate) : '-'}</span>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {reasonModalRecord.coverReason && (
                                        <pre
                                            className="font-sans text-sm text-gray-600 dark:text-gray-300 mt-2"
                                            style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
                                        >
                                            {reasonModalRecord.coverReason}
                                        </pre>
                                    )}
                                </div>
                            )}
                            {reasonModalRecord.freeLeaveReason && (
                                <div>
                                    <div className="text-xs font-semibold text-blue-500 uppercase mb-1 pt-4">
                                        Free Leave ({reasonModalRecord.freeLeaveDays || 0} day(s))
                                    </div>
                                    <pre
                                        className="font-sans text-sm"
                                        style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}
                                    >
                                        {reasonModalRecord.freeLeaveReason}
                                    </pre>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <ToastContainer position="top-center" className="!z-[99999]" />
        </div>
    );
};

export default TempSalary;